import { useCallback, useRef } from 'react';
import { Center, Column, CustomSpinner, EmptyScreen, Text } from '~/commonComponents';
import { CreatePostingButton } from '~/components/PermanentJobsV2/CreatePostingButton';
import { IDraftJobPostListViewEntity } from '~/domain/PermanentJobs';
import { useToggle } from '~/hooks';
import BriefcaseEmptyScreenIcon from '~/images/briefcase-empty-screen-icon.svg';
import { Colors } from '~/themes/colors';
import { DraftJobCard } from '../JobCard';
import { DraftTableStyles } from './Constants';
import { DeleteDraftPopupConfirmation } from './DeleteDraftPopup';

interface IDraftTableProps {
  data: IDraftJobPostListViewEntity[];
  isLoading: boolean;
}

export const DraftTable = ({ data, isLoading }: IDraftTableProps) => {
  const [isDeleteOpen, controlsDelete] = useToggle();
  const targetedJobPost = useRef<string | undefined>(undefined);

  const isEmptyData = data.length === 0;

  const handleDeleteClicked = useCallback(
    (candidate: string) => {
      targetedJobPost.current = candidate;
      controlsDelete.setOn();
    },
    [controlsDelete],
  );

  if (!isLoading && isEmptyData) {
    return (
      <EmptyScreen
        Icon={() => (
          <img
            alt="briefcase-empty-screen-icon"
            src={BriefcaseEmptyScreenIcon}
            height={170}
            width={170}
          />
        )}
        title="No drafts yet!"
        description={
          <Column alignItems="center" gap={48}>
            <Text color={Colors.neutral_500}>
              You don’t have any saved drafts. Start creating a<br />
              job post, and your progress will be saved here
              <br />
              until you’re ready to publish.
            </Text>
            <CreatePostingButton />
          </Column>
        }
      />
    );
  }

  return (
    <>
      <table style={DraftTableStyles.Table}>
        <thead>
          <tr>
            <th style={DraftTableStyles.TableHeader.JobsCol}>
              <Text bold color={Colors.primary_500} fontSize={18}>
                Jobs
              </Text>
            </th>
            <th style={DraftTableStyles.TableHeader.ActionCol} />
          </tr>
        </thead>
        <tbody>
          {!isLoading &&
            data.map((it: IDraftJobPostListViewEntity) => (
              <tr key={it.id}>
                <td colSpan={2}>
                  <DraftJobCard {...it} onDeleteDraft={handleDeleteClicked} />
                </td>
              </tr>
            ))}
        </tbody>
      </table>
      {isLoading && (
        <Center marginBlock={32}>
          <CustomSpinner isLoading size={32} />
        </Center>
      )}
      {isDeleteOpen && targetedJobPost.current && (
        <DeleteDraftPopupConfirmation
          controls={controlsDelete}
          jobPostingId={targetedJobPost.current}
        />
      )}
    </>
  );
};
